import { Connection, PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';
import BN from 'bn.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountInstruction,
  getAssociatedTokenAddressSync,
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import { parseError } from '../utils/errorParser';
import {
  createSubmittedFailureResult,
  createSubmittedUnknownResult,
  createSwapSuccessResult,
} from './submissionResult';

const parseAmountToAtomic = (amount, decimals) => {
  const [whole = '0', fraction = ''] = String(amount).split('.');
  const paddedFraction = `${fraction}${'0'.repeat(decimals)}`.slice(0, decimals);
  return new BN(`${whole}${paddedFraction}`);
};

const u64LeBuffer = value => value.toArrayLike(Buffer, 'le', 8);
const PUBLIC_SOLANA_RPC_URLS = [
  'https://solana-rpc.publicnode.com',
  'https://api.mainnet-beta.solana.com',
];
const SIGNATURE_CONFIRMATION_TIMEOUT_MS = 45000;
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const formatSimulationError = (err, logs = [], context = null) => {
  const instructionError = Array.isArray(err?.InstructionError)
    ? err.InstructionError[1]
    : err?.InstructionError;
  const lastProgramLog = logs
    .slice()
    .reverse()
    .find(line => line.includes('Program log:') || line.includes('Program failed:'));

  const detail =
    (typeof instructionError === 'string' && instructionError) ||
    (instructionError?.Custom ? `Custom(${instructionError.Custom})` : null) ||
    (lastProgramLog ? lastProgramLog.replace(/^.*Program log:\s*/, '') : null) ||
    JSON.stringify(err);

  const contextSuffix = context ? ` | context=${JSON.stringify(context)}` : '';
  return `Simulation failed on Solana: ${detail}${contextSuffix}`;
};
const getTokenProgramId = value => new PublicKey(value || TOKEN_PROGRAM_ID.toBase58());
const getUsdnTokenProgramId = config =>
  getTokenProgramId(config.usdnTokenProgramId || TOKEN_2022_PROGRAM_ID.toBase58());
const getInputTokenProgramId = inputAsset => getTokenProgramId(inputAsset?.tokenProgramId);

const validateSolanaSwapConfig = (config, inputAsset) => {
  const missing = [];

  if (!config.swapProgramId) missing.push('REACT_APP_SOL_TREASURY_SWAP_PROGRAM_ID');
  if (!config.usdnMint) {
    missing.push('REACT_APP_SOL_USDN_MINT');
  }
  if (!config.usdnTokenProgramId) {
    missing.push('REACT_APP_SOL_USDN_TOKEN_PROGRAM_ID');
  }
  if (!inputAsset?.tokenProgramId) {
    missing.push(`REACT_APP_SOL_${inputAsset?.key || 'TOKEN'}_TOKEN_PROGRAM_ID`);
  }
  if (!config.instructionDiscriminatorHex) {
    missing.push('REACT_APP_SOL_SWAP_INSTRUCTION_DISCRIMINATOR');
  }
  if (missing.length) {
    throw new Error(`Solana swap is not configured. Set: ${missing.join(', ')}`);
  }
};
const deriveSwapStatePda = programId =>
  PublicKey.findProgramAddressSync([Buffer.from('swap_state')], programId)[0];
const deriveStablecoinEntryPda = (programId, stablecoinMint) =>
  PublicKey.findProgramAddressSync(
    [Buffer.from('stablecoin'), stablecoinMint.toBuffer()],
    programId
  )[0];
const ensureUserInputAccount = async ({ connection, userInputAta, inputMint }) => {
  const userInputInfo = await connection.getAccountInfo(userInputAta);
  if (!userInputInfo) {
    throw new Error(
      `Input token account not found for selected token mint ${inputMint.toBase58()}. Expected ATA: ${userInputAta.toBase58()}`
    );
  }
  return userInputInfo;
};
const assertSwapAccountsExist = async ({
  connection,
  owner,
  programId,
  swapStatePda,
  stablecoinEntryPda,
  treasuryInput,
  treasuryOutput,
  inputMint,
  usdnMint,
  inputTokenProgramId,
  usdnTokenProgramId,
}) => {
  const [
    ownerInfo,
    programInfo,
    swapStateInfo,
    stablecoinEntryInfo,
    treasuryInputInfo,
    treasuryOutputInfo,
    inputMintInfo,
    usdnMintInfo,
    treasuryInputParsed,
    treasuryOutputParsed,
  ] = await Promise.all([
    connection.getAccountInfo(owner),
    connection.getAccountInfo(programId),
    connection.getAccountInfo(swapStatePda),
    connection.getAccountInfo(stablecoinEntryPda),
    connection.getAccountInfo(treasuryInput),
    connection.getAccountInfo(treasuryOutput),
    connection.getAccountInfo(inputMint),
    connection.getAccountInfo(usdnMint),
    connection.getParsedAccountInfo(treasuryInput),
    connection.getParsedAccountInfo(treasuryOutput),
  ]);

  if (!ownerInfo) {
    throw new Error(
      `Connected wallet account does not exist on this RPC yet: ${owner.toBase58()}. Fund it with SOL on the active cluster and retry.`
    );
  }
  if ((ownerInfo.lamports || 0) === 0) {
    throw new Error(
      `Connected wallet has 0 SOL for fees on this network: ${owner.toBase58()}. Fund SOL and retry.`
    );
  }
  if (!programInfo || !programInfo.executable) {
    throw new Error(
      `Configured swap program is not deployed or executable: ${programId.toBase58()}`
    );
  }
  if (!swapStateInfo) {
    throw new Error(`Swap state PDA not found for program: ${swapStatePda.toBase58()}`);
  }
  if (!stablecoinEntryInfo) {
    throw new Error(
      `Stablecoin is not registered in treasury swap program. Missing entry PDA: ${stablecoinEntryPda.toBase58()}`
    );
  }
  if (!treasuryInputInfo) {
    throw new Error(`Treasury stablecoin account not found: ${treasuryInput.toBase58()}`);
  }
  if (!treasuryOutputInfo) {
    throw new Error(`Treasury USDN account not found: ${treasuryOutput.toBase58()}`);
  }
  if (!inputMintInfo) {
    throw new Error(`Input mint not found on this network: ${inputMint.toBase58()}`);
  }
  if (!usdnMintInfo) {
    throw new Error(`USDN mint not found on this network: ${usdnMint.toBase58()}`);
  }

  const inputTokenProgramAddress = inputTokenProgramId.toBase58();
  const usdnTokenProgramAddress = usdnTokenProgramId.toBase58();
  const treasuryInputOwner = treasuryInputInfo.owner?.toBase58?.() || '';
  const treasuryOutputOwner = treasuryOutputInfo.owner?.toBase58?.() || '';
  const inputMintOwner = inputMintInfo.owner?.toBase58?.() || '';
  const usdnMintOwner = usdnMintInfo.owner?.toBase58?.() || '';
  const treasuryInputTokenOwner = treasuryInputParsed?.value?.data?.parsed?.info?.owner || '';
  const treasuryOutputTokenOwner = treasuryOutputParsed?.value?.data?.parsed?.info?.owner || '';

  if (treasuryInputOwner !== inputTokenProgramAddress) {
    throw new Error(
      `Treasury stablecoin account has wrong owner program: ${treasuryInputOwner}. Expected ${inputTokenProgramAddress}.`
    );
  }
  if (treasuryOutputOwner !== usdnTokenProgramAddress) {
    throw new Error(
      `Treasury USDN account has wrong owner program: ${treasuryOutputOwner}. Expected ${usdnTokenProgramAddress}.`
    );
  }
  if (inputMintOwner !== inputTokenProgramAddress) {
    throw new Error(
      `Input mint has wrong owner program: ${inputMintOwner}. Expected ${inputTokenProgramAddress}.`
    );
  }
  if (usdnMintOwner !== usdnTokenProgramAddress) {
    throw new Error(
      `USDN mint has wrong owner program: ${usdnMintOwner}. Expected ${usdnTokenProgramAddress}.`
    );
  }
  if (treasuryInputTokenOwner !== swapStatePda.toBase58()) {
    throw new Error(
      `Treasury stablecoin account has wrong token owner: ${treasuryInputTokenOwner}. Expected ${swapStatePda.toBase58()}.`
    );
  }
  if (treasuryOutputTokenOwner !== swapStatePda.toBase58()) {
    throw new Error(
      `Treasury USDN account has wrong token owner: ${treasuryOutputTokenOwner}. Expected ${swapStatePda.toBase58()}.`
    );
  }
};
const deriveTreasuryAccounts = ({
  swapStatePda,
  inputMint,
  usdnMint,
  inputTokenProgramId,
  usdnTokenProgramId,
}) => {
  const treasuryInput = getAssociatedTokenAddressSync(
    inputMint,
    swapStatePda,
    true,
    inputTokenProgramId
  );
  const treasuryOutput = getAssociatedTokenAddressSync(
    usdnMint,
    swapStatePda,
    true,
    usdnTokenProgramId
  );
  return { treasuryInput, treasuryOutput };
};

export const createSolanaAdapter = config => {
  let programConnectionPromise = null;

  const getCandidateRpcUrls = () => [
    ...new Set([config.rpcUrl, ...PUBLIC_SOLANA_RPC_URLS].filter(Boolean)),
  ];

  const getCapabilities = () => ({
    requiresApproval: false,
    supportsSimulation: true,
    supportsNetworkSwitch: false,
  });

  const getReadSourceLabel = rpcUrl =>
    rpcUrl === config.rpcUrl ? `configured chain rpc (${rpcUrl})` : `fallback rpc (${rpcUrl})`;

  const getProgramConnection = async () => {
    if (programConnectionPromise) return programConnectionPromise;

    programConnectionPromise = (async () => {
      const candidateRpcUrls = getCandidateRpcUrls();
      const programId = new PublicKey(config.swapProgramId);

      for (const rpcUrl of candidateRpcUrls) {
        try {
          const candidateConnection = new Connection(rpcUrl, 'confirmed');
          const programInfo = await candidateConnection.getAccountInfo(programId);
          if (programInfo?.executable) return candidateConnection;
        } catch (error) {
          // Ignore probe failures and continue with next RPC candidate.
        }
      }

      throw new Error(
        `Configured swap program is not deployed or executable on configured/public Solana RPCs: ${config.swapProgramId}`
      );
    })();

    return programConnectionPromise;
  };

  const withReadConnectionFallback = async read => {
    const errors = [];
    for (const rpcUrl of getCandidateRpcUrls()) {
      try {
        return await read(new Connection(rpcUrl, 'confirmed'), rpcUrl);
      } catch (error) {
        errors.push(`${rpcUrl}: ${error?.message || String(error)}`);
      }
    }
    throw new Error(
      `Solana RPC balance reads failed on all configured endpoints: ${errors.join(' | ')}`
    );
  };

  const getSignatureStatusWithFallback = async signature =>
    withReadConnectionFallback(async connection => {
      const response = await connection.getSignatureStatuses([signature], {
        searchTransactionHistory: true,
      });
      return response?.value?.[0] || null;
    });

  const readSubmittedSignatureStatus = async signature => {
    const status = await getSignatureStatusWithFallback(signature);
    if (status?.err) return { ok: false, failed: true, error: status.err };
    if (
      status?.confirmationStatus === 'confirmed' ||
      status?.confirmationStatus === 'finalized' ||
      (status && status.confirmations !== null)
    ) {
      return { ok: true };
    }
    return null;
  };

  const pollSubmittedSignatureStatus = async signature => {
    const startedAt = Date.now();
    let confirmationError = null;

    while (Date.now() - startedAt < SIGNATURE_CONFIRMATION_TIMEOUT_MS) {
      try {
        const result = await readSubmittedSignatureStatus(signature);
        if (result) return result;
      } catch (error) {
        confirmationError = confirmationError || error;
      }
      await sleep(1500);
    }

    return { ok: false, failed: false, error: confirmationError };
  };

  const confirmSubmittedSignature = async (connection, signature) => {
    try {
      const confirmation = await connection.confirmTransaction(signature, 'confirmed');
      if (confirmation?.value?.err) {
        return { ok: false, failed: true, error: confirmation.value.err };
      }
      if (confirmation?.value) return { ok: true };
    } catch (error) {
      return { ok: false, failed: false, error };
    }
    return null;
  };

  const waitForSubmittedSignature = async (connection, signature) => {
    const confirmationResult = await Promise.race([
      confirmSubmittedSignature(connection, signature),
      pollSubmittedSignatureStatus(signature),
    ]);

    if (confirmationResult) {
      return confirmationResult;
    }

    return pollSubmittedSignatureStatus(signature);
  };

  const getTokenBalance = async ({
    connection,
    rpcUrl,
    owner,
    mint,
    tokenProgramId,
    label,
    attempts,
  }) => {
    const tokenAccount = getAssociatedTokenAddressSync(mint, owner, false, tokenProgramId);
    const attempt = {
      source: getReadSourceLabel(rpcUrl),
      account: tokenAccount.toBase58(),
    };

    try {
      const accountInfo = await connection.getAccountInfo(tokenAccount);
      if (!accountInfo) {
        attempts.push({ ...attempt, result: '0', reason: 'token account not found' });
        return '0';
      }

      const response = await connection.getTokenAccountBalance(tokenAccount);
      const result = response?.value?.uiAmountString || '0';
      attempts.push({ ...attempt, result });
      return result;
    } catch (error) {
      attempts.push({ ...attempt, error: error?.message || String(error) });
      throw new Error(
        `${label} balance read failed for ATA ${tokenAccount.toBase58()}: ${error?.message || String(error)}`
      );
    }
  };

  const getBalances = async ({ account, inputAsset }) => {
    validateSolanaSwapConfig(config, inputAsset);
    const owner = new PublicKey(account);
    const inputMint = new PublicKey(inputAsset.mint);
    const usdnMint = new PublicKey(config.usdnMint);
    const inputTokenProgramId = getInputTokenProgramId(inputAsset);
    const usdnTokenProgramId = getUsdnTokenProgramId(config);
    const diagnostics = {
      account,
      inputAttempts: [],
      outputAttempts: [],
    };

    return withReadConnectionFallback(async (connection, rpcUrl) => {
      const inputBalance = await getTokenBalance({
        connection,
        rpcUrl,
        owner,
        mint: inputMint,
        tokenProgramId: inputTokenProgramId,
        label: inputAsset.key || 'Input token',
        attempts: diagnostics.inputAttempts,
      });
      let outputBalance = '0';
      try {
        outputBalance = await getTokenBalance({
          connection,
          rpcUrl,
          owner,
          mint: usdnMint,
          tokenProgramId: usdnTokenProgramId,
          label: 'USDN',
          attempts: diagnostics.outputAttempts,
        });
      } catch {
        outputBalance = '0';
      }
      return { inputBalance, outputBalance, diagnostics };
    });
  };

  const checkAllowanceLikeCapability = async () => ({ enough: true });

  const approveIfRequired = async () => ({
    ok: true,
    status: 'Approval not required on Solana',
    statusType: 'info',
  });

  const simulateSwap = async ({ account, inputAsset, amount }) => {
    validateSolanaSwapConfig(config, inputAsset);
    const connection = await getProgramConnection();
    const owner = new PublicKey(account);
    const inputMint = new PublicKey(inputAsset.mint);
    const usdnMint = new PublicKey(config.usdnMint);
    const inputTokenProgramId = getInputTokenProgramId(inputAsset);
    const usdnTokenProgramId = getUsdnTokenProgramId(config);
    const programId = new PublicKey(config.swapProgramId);
    const swapStatePda = deriveSwapStatePda(programId);
    const stablecoinEntryPda = deriveStablecoinEntryPda(programId, inputMint);
    const { treasuryInput, treasuryOutput } = deriveTreasuryAccounts({
      swapStatePda,
      inputMint,
      usdnMint,
      inputTokenProgramId,
      usdnTokenProgramId,
    });

    const userInputAta = getAssociatedTokenAddressSync(
      inputMint,
      owner,
      false,
      inputTokenProgramId
    );
    const userOutputAta = getAssociatedTokenAddressSync(usdnMint, owner, false, usdnTokenProgramId);
    await assertSwapAccountsExist({
      connection,
      owner,
      programId,
      swapStatePda,
      stablecoinEntryPda,
      treasuryInput,
      treasuryOutput,
      inputMint,
      usdnMint,
      inputTokenProgramId,
      usdnTokenProgramId,
    });
    const discriminatorBytes = Buffer.from(config.instructionDiscriminatorHex, 'hex');
    const amountAtomic = parseAmountToAtomic(amount, inputAsset.decimals);
    const instructionData = Buffer.concat([discriminatorBytes, u64LeBuffer(amountAtomic)]);

    const tx = new Transaction();

    await ensureUserInputAccount({ connection, userInputAta, inputMint });

    const userOutputInfo = await connection.getAccountInfo(userOutputAta);
    const userOutputAtaExistsBefore = !!userOutputInfo;
    if (!userOutputInfo) {
      tx.add(
        createAssociatedTokenAccountInstruction(
          owner,
          userOutputAta,
          owner,
          usdnMint,
          usdnTokenProgramId,
          ASSOCIATED_TOKEN_PROGRAM_ID
        )
      );
    }

    tx.add(
      new TransactionInstruction({
        programId,
        keys: [
          { pubkey: swapStatePda, isSigner: false, isWritable: true },
          { pubkey: stablecoinEntryPda, isSigner: false, isWritable: false },
          { pubkey: inputMint, isSigner: false, isWritable: false },
          { pubkey: usdnMint, isSigner: false, isWritable: false },
          { pubkey: userInputAta, isSigner: false, isWritable: true },
          { pubkey: userOutputAta, isSigner: false, isWritable: true },
          { pubkey: treasuryInput, isSigner: false, isWritable: true },
          { pubkey: treasuryOutput, isSigner: false, isWritable: true },
          { pubkey: owner, isSigner: true, isWritable: true },
          { pubkey: inputTokenProgramId, isSigner: false, isWritable: false },
          { pubkey: usdnTokenProgramId, isSigner: false, isWritable: false },
        ],
        data: instructionData,
      })
    );

    tx.feePayer = owner;
    const { blockhash } = await connection.getLatestBlockhash('confirmed');
    tx.recentBlockhash = blockhash;
    const simulation = await connection.simulateTransaction(tx, undefined, {
      sigVerify: false,
      commitment: 'confirmed',
    });
    if (simulation?.value?.err) {
      throw new Error(
        formatSimulationError(simulation.value.err, simulation.value.logs || [], {
          programId: programId.toBase58(),
          owner: owner.toBase58(),
          inputMint: inputMint.toBase58(),
          usdnMint: usdnMint.toBase58(),
          userInputAta: userInputAta.toBase58(),
          userOutputAta: userOutputAta.toBase58(),
          userOutputAtaExistsBefore,
          treasuryInput: treasuryInput.toBase58(),
          treasuryOutput: treasuryOutput.toBase58(),
          inputTokenProgram: inputTokenProgramId.toBase58(),
          usdnTokenProgram: usdnTokenProgramId.toBase58(),
          swapStatePda: swapStatePda.toBase58(),
          stablecoinEntryPda: stablecoinEntryPda.toBase58(),
        })
      );
    }
    return { ok: true };
  };

  const submitSwap = async ({ wallet, account, inputAsset, amount, onSubmitted }) => {
    let txHash = '';
    try {
      validateSolanaSwapConfig(config, inputAsset);
      const connection = await getProgramConnection();
      if (!wallet?.sendTransaction) {
        return {
          ok: false,
          status: 'Please connect a Solana wallet that supports signing.',
          statusType: 'error',
        };
      }

      const owner = new PublicKey(account);
      const inputMint = new PublicKey(inputAsset.mint);
      const usdnMint = new PublicKey(config.usdnMint);
      const inputTokenProgramId = getInputTokenProgramId(inputAsset);
      const usdnTokenProgramId = getUsdnTokenProgramId(config);
      const programId = new PublicKey(config.swapProgramId);
      const swapStatePda = deriveSwapStatePda(programId);
      const stablecoinEntryPda = deriveStablecoinEntryPda(programId, inputMint);
      const { treasuryInput, treasuryOutput } = deriveTreasuryAccounts({
        swapStatePda,
        inputMint,
        usdnMint,
        inputTokenProgramId,
        usdnTokenProgramId,
      });
      await assertSwapAccountsExist({
        connection,
        owner,
        programId,
        swapStatePda,
        stablecoinEntryPda,
        treasuryInput,
        treasuryOutput,
        inputMint,
        usdnMint,
        inputTokenProgramId,
        usdnTokenProgramId,
      });

      const userInputAta = getAssociatedTokenAddressSync(
        inputMint,
        owner,
        false,
        inputTokenProgramId
      );
      const userOutputAta = getAssociatedTokenAddressSync(
        usdnMint,
        owner,
        false,
        usdnTokenProgramId
      );

      const tx = new Transaction();

      await ensureUserInputAccount({ connection, userInputAta, inputMint });

      const userOutputInfo = await connection.getAccountInfo(userOutputAta);
      if (!userOutputInfo) {
        tx.add(
          createAssociatedTokenAccountInstruction(
            owner,
            userOutputAta,
            owner,
            usdnMint,
            usdnTokenProgramId,
            ASSOCIATED_TOKEN_PROGRAM_ID
          )
        );
      }

      const discriminatorBytes = Buffer.from(config.instructionDiscriminatorHex, 'hex');
      const amountAtomic = parseAmountToAtomic(amount, inputAsset.decimals);
      const instructionData = Buffer.concat([discriminatorBytes, u64LeBuffer(amountAtomic)]);

      tx.add(
        new TransactionInstruction({
          programId,
          keys: [
            { pubkey: swapStatePda, isSigner: false, isWritable: true },
            { pubkey: stablecoinEntryPda, isSigner: false, isWritable: false },
            { pubkey: inputMint, isSigner: false, isWritable: false },
            { pubkey: usdnMint, isSigner: false, isWritable: false },
            { pubkey: userInputAta, isSigner: false, isWritable: true },
            { pubkey: userOutputAta, isSigner: false, isWritable: true },
            { pubkey: treasuryInput, isSigner: false, isWritable: true },
            { pubkey: treasuryOutput, isSigner: false, isWritable: true },
            { pubkey: owner, isSigner: true, isWritable: true },
            { pubkey: inputTokenProgramId, isSigner: false, isWritable: false },
            { pubkey: usdnTokenProgramId, isSigner: false, isWritable: false },
          ],
          data: instructionData,
        })
      );

      txHash = await wallet.sendTransaction(tx, connection);
      if (txHash && typeof onSubmitted === 'function') {
        onSubmitted(txHash);
      }

      const confirmation = await waitForSubmittedSignature(connection, txHash);
      if (confirmation.ok) {
        return createSwapSuccessResult(txHash);
      }
      if (confirmation.failed) {
        return createSubmittedFailureResult(txHash, confirmation.error);
      }

      return createSubmittedUnknownResult({
        txHash,
        explorerName: 'Solana Explorer',
        error: confirmation.error,
      });
    } catch (error) {
      if (txHash) {
        return createSubmittedUnknownResult({
          txHash,
          explorerName: 'Solana Explorer',
          error,
        });
      }
      return {
        ok: false,
        status: parseError(error),
        statusType: 'error',
      };
    }
  };

  return {
    getCapabilities,
    getBalances,
    checkAllowanceLikeCapability,
    approveIfRequired,
    simulateSwap,
    submitSwap,
  };
};
